import * as React from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  createUser,
  NO_PERMISSION_TEXT,
  deleteUser,
  formatPhone,
  getUserOptions,
  listUsers,
  setUserActive,
  updateUser,
  userAdminErrorText,
  type ManagedUser,
  type UserForm,
  type UserOptions,
} from '@/services/usersAdminService'
import UserFormDialog from './UserFormDialog'
import ConfirmDeleteDialog from '@/components/Analytics/ConfirmDeleteDialog'
import DeleteOutlineRoundedIcon from '@assets/Settings/Delete2.svg'
import EditIcon from '@assets/Settings/EditIcons.svg'
import PersonOutlineRoundedIcon from '@assets/person_asignee_icon.svg'

type Load =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'ready'; users: ManagedUser[]; options: UserOptions }

// The dialog that is open, if any. Only one can be.
type Open =
  | { kind: 'none' }
  | { kind: 'form'; user: ManagedUser | null }
  | { kind: 'delete'; user: ManagedUser }

const byName = (a: ManagedUser, b: ManagedUser) =>
  a.displayName.localeCompare(b.displayName, 'he')

const COLUMNS = [
  { label: 'משתמש', width: '17%' },
  { label: 'מייל', width: '22%' },
  { label: 'טלפון', width: '13%' },
  { label: 'אתרים', width: '15%' },
  { label: 'קבוצת הרשאות', width: '17%' },
  { label: 'פעיל / כבוי', width: '8%' },
  { label: '', width: '8%' },
]

export default function UsersPermissionsTable() {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [load, setLoad] = React.useState<Load>({ kind: 'loading' })
  const [open, setOpen] = React.useState<Open>({ kind: 'none' })
  const [busyIds, setBusyIds] = React.useState<number[]>([])
  const [notice, setNotice] = React.useState('')

  const fetchAll = React.useCallback(() => {
    let cancelled = false
    setLoad({ kind: 'loading' })
    Promise.all([listUsers(), getUserOptions()])
      .then(([users, options]) => {
        if (!cancelled) setLoad({ kind: 'ready', users, options })
      })
      .catch(err => {
        if (cancelled) return
        console.error('[UsersPermissionsTable] Failed to load users:', err)
        setLoad({ kind: 'failed' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  React.useEffect(() => fetchAll(), [fetchAll])

  const putRow = (user: ManagedUser) =>
    setLoad(prev =>
      prev.kind === 'ready'
        ? {
            ...prev,
            users: [
              ...prev.users.filter(u => u.userId !== user.userId),
              user,
            ].sort(byName),
          }
        : prev
    )

  const saveForm = async (form: UserForm) => {
    if (open.kind !== 'form') return
    try {
      const saved = open.user
        ? await updateUser(open.user.userId, form)
        : await createUser(form)
      putRow(saved)
      setOpen({ kind: 'none' })
    } catch (err) {
      throw new Error(userAdminErrorText(err, 'השמירה נכשלה. נסו שוב'))
    }
  }

  const toggleActive = async (user: ManagedUser, active: boolean) => {
    setNotice('')
    setBusyIds(ids => [...ids, user.userId])
    try {
      putRow(await setUserActive(user.userId, active))
    } catch (err) {
      setNotice(userAdminErrorText(err, 'עדכון הסטטוס נכשל. נסו שוב'))
    } finally {
      setBusyIds(ids => ids.filter(id => id !== user.userId))
    }
  }

  const deletedNameRef = React.useRef('')
  if (open.kind === 'delete') deletedNameRef.current = open.user.displayName
  const deletedName = deletedNameRef.current

  const confirmDelete = async () => {
    if (open.kind !== 'delete') return
    const { user } = open
    setOpen({ kind: 'none' })
    setNotice('')
    try {
      await deleteUser(user.userId)
      setLoad(prev =>
        prev.kind === 'ready'
          ? { ...prev, users: prev.users.filter(u => u.userId !== user.userId) }
          : prev
      )
    } catch (err) {
      setNotice(userAdminErrorText(err, 'מחיקת המשתמש נכשלה. נסו שוב'))
    }
  }

  const textColor = isDarkMode ? '#FFFFFF' : '#003F61'
  const subTextColor = isDarkMode ? '#94A3B8' : '#8F98A5'
  const borderColor = isDarkMode ? '#374151' : '#E4E7EB'
  const cellText = {
    color: subTextColor,
    fontSize: '14px',
    fontFamily: 'Rubik, sans-serif',
  }
  const iconFilter = isDarkMode
    ? 'brightness(0) invert(1) opacity(0.7)'
    : 'none'

  const options =
    load.kind === 'ready'
      ? load.options
      : { sites: [], roles: [], canEditUsers: false }
  const siteNames = (user: ManagedUser) =>
    user.siteIds
      .map(id => options.sites.find(s => s.siteId === id)?.siteName)
      .filter(Boolean)
      .join(', ')
  const groupNames = (user: ManagedUser) =>
    user.roleIds
      .map(id => options.roles.find(r => r.roleId === id)?.name)
      .filter(Boolean)
      .join(', ')

  const canEdit = options.canEditUsers
  const disabledIconSx = { '&.Mui-disabled': { opacity: 0.4 } }
  // A disabled control gets no pointer events, so the tooltip hangs on a wrapper around it.
  const editGate = (control: React.ReactElement, allowedTitle?: string) =>
    canEdit ? (
      allowedTitle ? (
        <Tooltip title={allowedTitle} arrow placement="top">
          {control}
        </Tooltip>
      ) : (
        control
      )
    ) : (
      <Tooltip title={NO_PERMISSION_TEXT} arrow placement="top">
        <span data-edit-gate style={{ display: 'inline-flex' }}>
          {control}
        </span>
      </Tooltip>
    )

  const message = (text: string, action?: React.ReactNode) => (
    <TableRow>
      <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
        <Typography sx={{ ...cellText }}>{text}</Typography>
        {action}
      </TableCell>
    </TableRow>
  )

  return (
    <Box
      sx={{
        direction: 'rtl',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: { xs: 0, sm: 2 },
        }}
      >
        <Button
          onClick={() => setOpen({ kind: 'form', user: null })}
          disabled={load.kind !== 'ready'}
          startIcon={
            <AddCircleOutlineIcon sx={{ color: '#72A4EB', ml: 1, mr: -0.5 }} />
          }
          sx={{
            border: `1px solid ${borderColor}`,
            borderRadius: '8px',
            px: 2.5,
            py: 1,
            bgcolor: isDarkMode ? '#202B40' : '#FFFFFF',
            color: textColor,
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            textTransform: 'none',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(114, 164, 235, 0.08)' : '#F1F8FE',
              borderColor: '#72A4EB',
            },
          }}
        >
          יצירת משתמש
        </Button>
      </Box>

      {notice && (
        <Typography
          role="alert"
          sx={{ ...cellText, color: '#FF5B5B', px: { xs: 0, sm: 2 } }}
        >
          {notice}
        </Typography>
      )}

      <Box sx={{ overflowX: 'auto', px: { xs: 0, sm: 2 }, width: '100%' }}>
        <Table
          size="small"
          aria-label="משתמשים והרשאות"
          sx={{
            minWidth: 760,
            '& .MuiTableCell-root': {
              borderBottom: `1px solid ${borderColor}`,
              py: 1.25,
              px: 1,
            },
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map(column => (
                <TableCell
                  key={column.label || 'actions'}
                  align="right"
                  sx={{ width: column.width }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: textColor,
                      fontFamily: 'Rubik, sans-serif',
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {load.kind === 'loading' &&
              message(
                'טוען משתמשים...',
                <CircularProgress size={20} sx={{ mt: 1, color: '#72A4EB' }} />
              )}
            {load.kind === 'failed' &&
              message(
                'טעינת המשתמשים נכשלה',
                <Button
                  onClick={fetchAll}
                  sx={{
                    mt: 1,
                    color: '#72A4EB',
                    fontFamily: 'Rubik, sans-serif',
                  }}
                >
                  נסו שוב
                </Button>
              )}
            {load.kind === 'ready' &&
              load.users.length === 0 &&
              message('אין משתמשים להצגה')}
            {load.kind === 'ready' &&
              load.users.map(user => (
                <TableRow key={user.userId} data-user={user.userName}>
                  <TableCell align="right">
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Box
                        component="img"
                        src={PersonOutlineRoundedIcon}
                        alt=""
                        sx={{ width: 18, height: 18, filter: iconFilter }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Rubik, sans-serif',
                          color: textColor,
                          fontSize: '14px',
                        }}
                      >
                        {user.displayName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={cellText}>
                    <Box
                      component="span"
                      dir="ltr"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {user.email}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={cellText}>
                    <Box
                      component="span"
                      dir="ltr"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {formatPhone(user.phone)}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={cellText}>
                    {siteNames(user)}
                  </TableCell>
                  <TableCell align="right" sx={cellText}>
                    {groupNames(user)}
                  </TableCell>
                  <TableCell align="right">
                    {editGate(
                      <Switch
                        checked={user.active}
                        disabled={
                          !canEdit ||
                          !user.manageable ||
                          user.self ||
                          busyIds.includes(user.userId)
                        }
                        onChange={e => toggleActive(user, e.target.checked)}
                        slotProps={{
                          input: {
                            role: 'switch',
                            'aria-label': `${user.displayName} פעיל`,
                          },
                        }}
                        sx={{
                          // The design puts the knob of an active user on the left, as an RTL switch.
                          transform: 'scaleX(-1)',
                          width: 44,
                          height: 24,
                          p: 0,
                          '& .MuiSwitch-switchBase': {
                            p: '3px',
                            '&.Mui-checked': {
                              transform: 'translateX(20px)',
                              color: '#FFFFFF',
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                              bgcolor: '#5B8DEF',
                              opacity: 1,
                            },
                            '&.Mui-disabled + .MuiSwitch-track': {
                              opacity: 0.5,
                            },
                          },
                          '& .MuiSwitch-thumb': {
                            width: 18,
                            height: 18,
                            boxShadow: 'none',
                            color: '#FFFFFF',
                          },
                          '& .MuiSwitch-track': {
                            borderRadius: 12,
                            bgcolor: isDarkMode ? '#475569' : '#D9DEE5',
                            opacity: 1,
                          },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {user.manageable && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {!user.self &&
                          editGate(
                            <IconButton
                              onClick={() => setOpen({ kind: 'delete', user })}
                              disabled={!canEdit}
                              size="small"
                              sx={disabledIconSx}
                              aria-label={`מחיקת משתמש ${user.displayName}`}
                            >
                              <Box
                                component="img"
                                src={DeleteOutlineRoundedIcon}
                                alt=""
                                sx={{ width: 16, height: 16 }}
                              />
                            </IconButton>,
                            'מחיקת משתמש'
                          )}
                        {editGate(
                          <IconButton
                            onClick={() => setOpen({ kind: 'form', user })}
                            disabled={!canEdit}
                            size="small"
                            sx={disabledIconSx}
                            aria-label={`עריכת משתמש ${user.displayName}`}
                          >
                            <Box
                              component="img"
                              src={EditIcon}
                              alt=""
                              sx={{ width: 16, height: 16, filter: iconFilter }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>

      <UserFormDialog
        open={open.kind === 'form'}
        user={open.kind === 'form' ? open.user : null}
        options={options}
        isDarkMode={isDarkMode}
        onClose={() => setOpen({ kind: 'none' })}
        onSave={saveForm}
      />

      <ConfirmDeleteDialog
        open={open.kind === 'delete'}
        onClose={() => setOpen({ kind: 'none' })}
        onConfirm={confirmDelete}
        title={
          <>
            למחוק את המשתמש{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {deletedName}
            </Box>
            ?
          </>
        }
        content={`המשתמש ${deletedName} לא יוכל יותר להתחבר למערכת. פעולה זו אינה ניתנת לביטול.`}
      />
    </Box>
  )
}
